import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import moment from 'moment';
import * as adminService from '../../services/admin';

const TaxConfiguration = () => {
    const [taxConfigurations, setTaxConfigurations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({
        id: null,
        tax_name: '',
        tax_type: '',
        tax_rate: '',
        tax_code: '',
        description: '',
        is_active: true,
        is_compound: false,
        effective_from: moment().format('YYYY-MM-DD'),
        effective_to: ''
    });

    const taxTypes = ['GST', 'TDS', 'VAT', 'Service Tax', 'Income Tax', 'Other'];

    useEffect(() => {
        loadTaxConfigurations();
    }, []);

    const loadTaxConfigurations = async () => {
        try {
            setLoading(true);
            const response = await adminService.listTaxConfigurations({});
            if (response.status === 'success') {
                setTaxConfigurations(response.data);
            }
        } catch (error) {
            console.error('Error loading tax configurations:', error);
            toast.error('Failed to load tax configurations');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleNewTax = () => {
        setFormData({
            id: null,
            tax_name: '',
            tax_type: '',
            tax_rate: '',
            tax_code: '',
            description: '',
            is_active: true,
            is_compound: false,
            effective_from: moment().format('YYYY-MM-DD'),
            effective_to: ''
        });
        setEditMode(false);
        setShowModal(true);
    };

    const handleEditTax = (tax) => {
        setFormData({
            id: tax.id,
            tax_name: tax.tax_name,
            tax_type: tax.tax_type,
            tax_rate: tax.tax_rate,
            tax_code: tax.tax_code,
            description: tax.description || '',
            is_active: tax.is_active,
            is_compound: tax.is_compound,
            effective_from: moment(tax.effective_from).format('YYYY-MM-DD'),
            effective_to: tax.effective_to ? moment(tax.effective_to).format('YYYY-MM-DD') : ''
        });
        setEditMode(true);
        setShowModal(true);
    };

    const handleSaveTax = async () => {
        // Validation
        if (!formData.tax_name || !formData.tax_type || !formData.tax_rate || !formData.tax_code || !formData.effective_from) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            setLoading(true);
            const data = {
                ...formData,
                tax_rate: parseFloat(formData.tax_rate)
            };

            let response;
            if (editMode) {
                response = await adminService.updateTaxConfiguration(formData.id, data);
            } else {
                response = await adminService.createTaxConfiguration(data);
            }

            if (response.status === 'success') {
                toast.success(`Tax configuration ${editMode ? 'updated' : 'created'} successfully`);
                setShowModal(false);
                loadTaxConfigurations();
            } else {
                toast.error(response.message || 'Failed to save tax configuration');
            }
        } catch (error) {
            console.error('Error saving tax configuration:', error);
            toast.error('Failed to save tax configuration');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTax = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This tax configuration will be deleted',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await adminService.deleteTaxConfiguration(id);
                if (response.status === 'success') {
                    toast.success('Tax configuration deleted successfully');
                    loadTaxConfigurations();
                } else {
                    toast.error(response.message || 'Failed to delete tax configuration');
                }
            } catch (error) {
                console.error('Error deleting tax configuration:', error);
                toast.error('Failed to delete tax configuration');
            }
        }
    };

    const columnDefs = [
        {
            headerName: 'Tax Name',
            field: 'tax_name',
            sortable: true,
            filter: true,
            width: 200
        },
        {
            headerName: 'Type',
            field: 'tax_type',
            sortable: true,
            filter: true,
            width: 120,
            cellRenderer: (params) => (
                <span className="badge bg-info">{params.value}</span>
            )
        },
        {
            headerName: 'Tax Code',
            field: 'tax_code',
            sortable: true,
            filter: true,
            width: 130
        },
        {
            headerName: 'Rate (%)',
            field: 'tax_rate',
            sortable: true,
            filter: true,
            width: 100,
            cellRenderer: (params) => `${parseFloat(params.value).toFixed(2)}%`
        },
        {
            headerName: 'Effective From',
            field: 'effective_from',
            sortable: true,
            filter: true,
            width: 140,
            cellRenderer: (params) => moment(params.value).format('DD-MMM-YYYY')
        },
        {
            headerName: 'Effective To',
            field: 'effective_to',
            sortable: true,
            filter: true,
            width: 140,
            cellRenderer: (params) => params.value ? moment(params.value).format('DD-MMM-YYYY') : 'Ongoing'
        },
        {
            headerName: 'Status',
            field: 'is_active',
            sortable: true,
            filter: true,
            width: 100,
            cellRenderer: (params) => (
                <span className={`badge ${params.value ? 'bg-success' : 'bg-secondary'}`}>
                    {params.value ? 'Active' : 'Inactive'}
                </span>
            )
        },
        {
            headerName: 'Compound',
            field: 'is_compound',
            sortable: true,
            width: 110,
            cellRenderer: (params) => params.value ? 'Yes' : 'No'
        },
        {
            headerName: 'Actions',
            field: 'id',
            width: 150,
            cellRenderer: (params) => (
                <div>
                    <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => handleEditTax(params.data)}
                    >
                        <i className="fa fa-edit"></i>
                    </button>
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteTax(params.value)}
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="container-fluid mt-3">
            <div className="card">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fa fa-calculator"></i> Tax Configuration
                    </h5>
                    <button className="btn btn-light btn-sm" onClick={handleNewTax}>
                        <i className="fa fa-plus"></i> New Tax
                    </button>
                </div>
                <div className="card-body">
                    <div className="ag-theme-alpine" style={{ height: '500px', width: '100%' }}>
                        <AgGridReact
                            rowData={taxConfigurations}
                            columnDefs={columnDefs}
                            pagination={true}
                            paginationPageSize={20}
                            domLayout='autoHeight'
                        />
                    </div>
                </div>
            </div>

            {/* Tax Modal */}
            {showModal && (
                <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editMode ? 'Edit Tax Configuration' : 'New Tax Configuration'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Tax Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="tax_name"
                                            value={formData.tax_name}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Tax Type *</label>
                                        <select
                                            className="form-select"
                                            name="tax_type"
                                            value={formData.tax_type}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">-- Select Type --</option>
                                            {taxTypes.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Tax Code *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="tax_code"
                                            value={formData.tax_code}
                                            onChange={handleInputChange}
                                            placeholder="e.g., CGST, SGST, TDS-194C"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Tax Rate (%) *</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="tax_rate"
                                            value={formData.tax_rate}
                                            onChange={handleInputChange}
                                            step="0.01"
                                            min="0"
                                            max="100"
                                        />
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Effective From *</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="effective_from"
                                            value={formData.effective_from}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Effective To</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="effective_to"
                                            value={formData.effective_to}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-12">
                                        <label className="form-label fw-bold">Description</label>
                                        <textarea
                                            className="form-control"
                                            name="description"
                                            rows="3"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                        ></textarea>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <div className="form-check form-switch">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                name="is_active"
                                                checked={formData.is_active}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label">
                                                Active
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-check form-switch">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                name="is_compound"
                                                checked={formData.is_compound}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label">
                                                Compound Tax (Tax on Tax)
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSaveTax}
                                    disabled={loading}
                                >
                                    <i className="fa fa-save"></i> Save Tax
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaxConfiguration;
